import { useEffect, useState } from "react";
import { api, type StructureType } from "../lib/api";

interface StructureTypeSelectorProps {
  selectedType: string;
  onSelect: (typeKey: string) => void;
  className?: string;
}

export function StructureTypeSelector({ selectedType, onSelect, className = "" }: StructureTypeSelectorProps) {
  const [types, setTypes] = useState<StructureType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTypes();
  }, []);

  async function loadTypes() {
    try {
      setLoading(true);
      const response = await api.listStructureTypes();
      setTypes(response.structure_types);
      setError(null);
    } catch (err) {
      console.error("Failed to load structure types:", err);
      setError("Fehler beim Laden der Struktur-Typen");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className={`text-center py-4 ${className}`}>
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-sm text-gray-600">Lade Typen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-4 ${className}`}>
        <p className="text-red-600">{error}</p>
        <button onClick={loadTypes} className="mt-2 text-sm text-blue-600 hover:underline">
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Wähle einen Struktur-Typ:
      </label>
      <div className="grid grid-cols-1 gap-3">
        {types.map((type) => (
          <button
            key={type.key}
            type="button"
            onClick={() => onSelect(type.key)}
            className={`
              relative flex items-start p-4 border-2 rounded-lg transition-all
              hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              ${
                selectedType === type.key
                  ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400"
                  : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400"
              }
            `}
          >
            <div className="flex-shrink-0 text-3xl mr-3">{type.icon || "📊"}</div>
            <div className="flex-1 text-left">
              <div className="flex items-center">
                <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {type.label}
                </span>
                {selectedType === type.key && (
                  <svg
                    className="ml-2 h-5 w-5 text-blue-600 dark:text-blue-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              {type.description && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{type.description}</p>
              )}
              {type.config.features && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {type.config.features.contacts && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      Kontakte
                    </span>
                  )}
                  {type.config.features.links && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      Links
                    </span>
                  )}
                  {type.config.features.decisionBranching && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                      Entscheidungen
                    </span>
                  )}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
